import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlankRounded";
import { Stack, ToggleButton, ToggleButtonGroup, Tooltip } from "@mui/material";
import ExpandAllLevelsIcon from "@entrilia/shared/icons/ExpandAllLevelsIcon";
import ExpandFirstLevelIcon from "@entrilia/shared/icons/ExpandFirstLevelIcon";
import RefreshGridButton from "../../../sv/components/viewer/common/RefreshGridButton.tsx";
import useBalanceSheetContext from "../contexts/BalanceSheetContext.tsx";
import type { BalanceSheetPriorPeriodType } from "../utils/balanceSheetPeriod.ts";
import {
  balanceSheetPriorPeriodOptionsByType,
  getBalanceSheetPriorPeriodOptionLabel,
  getBalanceSheetPriorPeriodTypeUiLabel,
} from "../utils/balanceSheetPeriod.ts";
import type { BalanceSheetExpandMode } from "../utils/tree.ts";

interface Props {
  expandMode: BalanceSheetExpandMode | null;
  onExpandModeChange: (mode: BalanceSheetExpandMode | null) => void;
}

const BalanceSheetTableToolbar = ({ expandMode, onExpandModeChange }: Props) => {
  const { refresh, isFetching, period, onPeriodTypeChange, onPeriodCountChange, hasDimensionBreakdown } =
    useBalanceSheetContext();

  const periodType = period.priorPeriodType;

  return (
    <Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "center" }}>
      <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
        <Tooltip
          title={
            hasDimensionBreakdown
              ? "The period type is currently unavailable due to applying dimension breakdown."
              : undefined
          }
          arrow
          disableInteractive
        >
          <ToggleButtonGroup
            value={period.priorPeriodType}
            exclusive
            size="small"
            disabled={hasDimensionBreakdown}
            onChange={(_, value: BalanceSheetPriorPeriodType | null) => {
              if (!value) {
                return;
              }
              onPeriodTypeChange(value);
            }}
          >
            <ToggleButton value="custom">{getBalanceSheetPriorPeriodTypeUiLabel("custom")}</ToggleButton>
            <ToggleButton value="month">{getBalanceSheetPriorPeriodTypeUiLabel("month")}</ToggleButton>
            <ToggleButton value="quarter">{getBalanceSheetPriorPeriodTypeUiLabel("quarter")}</ToggleButton>
            <ToggleButton value="year">{getBalanceSheetPriorPeriodTypeUiLabel("year")}</ToggleButton>
          </ToggleButtonGroup>
        </Tooltip>

        {periodType !== "custom" && (
          <Tooltip
            title={
              hasDimensionBreakdown
                ? "The period count is currently unavailable due to applying dimension breakdown."
                : undefined
            }
            arrow
            disableInteractive
          >
            <ToggleButtonGroup
              value={period.priorPeriodCount}
              exclusive
              size="small"
              disabled={hasDimensionBreakdown}
              onChange={(_, value: number | null) => {
                if (!value) {
                  return;
                }
                onPeriodCountChange(value);
              }}
            >
              {balanceSheetPriorPeriodOptionsByType[periodType].map((option) => (
                <ToggleButton key={option} value={option}>
                  {getBalanceSheetPriorPeriodOptionLabel(periodType, option)}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Tooltip>
        )}

        <ToggleButtonGroup
          value={expandMode}
          exclusive
          size="small"
          sx={{ height: 34 }}
          onChange={(_, value: BalanceSheetExpandMode | null) => {
            if (!value) {
              return;
            }
            onExpandModeChange(value);
          }}
        >
          <ToggleButton value="collapsed">
            <ExpandAllLevelsIcon fontSize="small" />
          </ToggleButton>
          <ToggleButton value="partial">
            <ExpandFirstLevelIcon fontSize="small" />
          </ToggleButton>
          <ToggleButton value="expanded">
            <CheckBoxOutlineBlankIcon fontSize="small" />
          </ToggleButton>
        </ToggleButtonGroup>
      </Stack>
      <Stack direction="row" spacing={0.5}>
        <RefreshGridButton onRefresh={refresh} disabled={isFetching} />
      </Stack>
    </Stack>
  );
};

export default BalanceSheetTableToolbar;
